import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import SettingsIcon from '@material-ui/icons/Settings';
import AppContainer from 'components/AppContainer';
import useSiteCopySelector from 'components/useSiteCopySelector';
import {
  isAssessmentSaved,
  getColorsConfig,
} from 'containers/Assessments/utils';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { getContentfulEntryUrl } from 'utils/contentfulService';
import MixPanel from 'utils/mixpanelService';
import {
  makeSelectAllowSignIn,
  makeSelectClientDetails,
  makeSelectTextDirection,
} from 'containers/Main/selectors';
import { makeSelectSavedAssessments } from 'containers/Assessments/selectors';
import { makeSelectAuth } from 'containers/Auth/selectors';
import { trackFavoriteAction } from 'containers/Favorites/utils';
import { saveAssessment } from 'containers/Assessments/actions';
import withSizes from 'react-sizes';
import RichTextReactRenderer from 'components/RichTextReactRenderer';
import PageIntroBlock from 'components/PageIntroBlock';
import ReactTooltip from 'react-tooltip';
import ShareWithDropdown from 'components/ShareWithDropdown';
import ScheduleIcon from '@material-ui/icons/Schedule';
import Button from '@material-ui/core/Button';
import TakeArrowWhite from 'containers/Assessments/images/assessment-arrow-white.svg';
import ContentBlock from 'components/ContentBlock';
import {
  CONTENT_BLOCK_POSITIONS,
  CONTENT_BLOCK_TYPES,
} from 'components/ContentBlock/utils';
import HeartEmptyIcon from './images/favorite-empty.svg';
import HeartEmptyMobileIcon from './images/favorite-empty-transparent.svg';
import HeartFullIcon from './images/favorite-full.svg';

const useStyles = makeStyles(theme => ({
  topContainer: {
    paddingTop: 20,
    paddingBottom: 40,
    backgroundColor: theme.palette.primary.bgColor,
  },
  badgeContainer: {
    display: 'flex',
    alignItems: 'flex-start',
  },
  badge: {
    color: theme.palette.primary.font,
  },
  button: {
    padding: 0,
    marginLeft: 16,
    backgroundColor: 'transparent',
    '&:hover': {
      background: 'none',
    },
    border: 0,
    borderRadius: 0,
  },
  favorite: {
    width: 20,
  },
  settings: {
    fontSize: 25,
  },
  title: {
    ...theme.typography.h2,
    color: theme.palette.primary.font,
    direction: ({ textDirection }) => textDirection,
  },
  introText: {
    '& > div': {
      width: '100%',
      '& p': {
        fontSize: 17,
        textAlign: 'left',
      },
    },
    '&::before': {
      content: "''",
    },
  },
  timeWrapper: {
    display: 'flex',
    alignItems: 'center',
    '& > svg': {
      width: 20,
      height: 20,
      marginRight: 8,
      fill: theme.palette.primary.font,
    },
  },
  time: {
    color: theme.palette.primary.font,
    height: 20,
  },
  shareWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  share: {
    color: '#4B4B4B',
    fontSize: 14,
    marginLeft: 20,
  },
  saveAsText: {
    color: '#4B4B4B',
    fontSize: 14,
    marginLeft: 5,
  },
  icon: {
    display: 'flex',
    fontSize: 20,
    '& > div': {
      display: 'flex',
      marginLeft: 8,
    },
    '& > div > svg > g > path': {
      fill: theme.palette.primary.dark,
    },
  },
  shareAndLikeWrapper: {
    background: '#fff',
    height: 39,
    borderRadius: 3,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    margin: '0 1px',
    maxWidth: 165,
    '& button': {
      marginLeft: 0,
    },
  },
  iconShareWrapper: {
    '& > button': {
      padding: 0,
      '& > span': {
        margin: '0 5px',
      },
    },
  },
  topIcon: {
    position: 'absolute',
    right: '5%',
  },
  mobileView: {
    paddingRight: '5%',
    paddingLeft: '5%',
  },
  iconBtn: {
    '&:hover': {
      background: 'none',
    },
  },
  startButton: {
    boxShadow: 'none',
    borderRadius: 6,
    padding: '12px 16px',
    '& img': {
      marginLeft: 10,
    },
  },
  startButtonContainer: {
    '&.sticky': {
      position: 'sticky',
      zIndex: 1,
      backgroundColor: theme.palette.primary.bgColor,
      width: '100%',
    },
  },
  clipContainer: {
    '&.visible': {
      position: 'fixed',
      width: '100%',
      height: '24px',
      background: `linear-gradient(0, ${theme.palette.primary.bgColor}00, ${theme.palette.primary.bgColor})`,
    },
  },
  contentBlockItem: {
    padding: 0,
    '&[data-style="Basic Content"]': {
      marginTop: '22px !important',
      '& > div': {
        padding: '0px',

        '& div': {
          padding: 0,
        },

        '& div > h5': {
          fontSize: '1.125rem',
          marginBottom: 10,
        },

        '& div p': {
          fontSize: 17,
        },
      },

      '&[data-color="Default White"] > div': {
        padding: 0,
        backgroundColor: 'transparent',
      },
    },
  },
}));

const stateSelector = createStructuredSelector({
  allowSignIn: makeSelectAllowSignIn(),
  auth: makeSelectAuth(),
  clientDetails: makeSelectClientDetails(),
  savedAssessments: makeSelectSavedAssessments(),
  textDirection: makeSelectTextDirection(),
});

const Header = ({
  isAdmin,
  isMobile,
  mobileShare,
  onStartButtonClick,
  assessment,
}) => {
  const dispatch = useDispatch();
  const [assessmentSiteCopy] = useSiteCopySelector(['assessment']);
  const {
    allowSignIn,
    auth,
    clientDetails,
    savedAssessments,
    textDirection,
  } = useSelector(stateSelector);
  const startButtonRef = useRef(null);
  const clipContainerRef = useRef(null);
  const blockRef = useRef(null);
  const classes = useStyles({
    textDirection,
  });

  useEffect(() => {
    if (!isMobile) {
      return undefined;
    }

    const handler = () => {
      const appHeader = document.getElementById('app-header');
      const startButtonContainer = startButtonRef.current;
      const clipContainer = clipContainerRef.current;
      const referenceBlock = blockRef.current;
      const startButtonRect = startButtonContainer.getBoundingClientRect();
      const referenceBlockRect = referenceBlock.getBoundingClientRect();

      const isButtonUnderHeader =
        referenceBlockRect.top <= appHeader.offsetHeight;

      if (isButtonUnderHeader) {
        startButtonContainer.classList.add('sticky');
        startButtonContainer.style.top = `${appHeader.offsetHeight}px`;
        clipContainer.classList.add('visible');
        clipContainer.style.top = `${Math.max(
          startButtonRect.bottom,
          appHeader.offsetHeight,
        )}px`;
      } else {
        clipContainer.classList.remove('visible');
        startButtonContainer.classList.remove('sticky');
      }
    };

    window.addEventListener('scroll', handler);
    return () => {
      window.removeEventListener('scroll', handler);
    };
  }, [isMobile]);

  const shouldShowShareButton = !_get(
    clientDetails,
    'metadata.hideLeftPanelActions',
    false,
  );

  const handleReadMoreLess = isMore => {
    const {
      fields: { name },
      sys: { id },
    } = assessment.items[0];

    MixPanel.track('Resource Page - Read More', {
      name,
      type: 'assessment',
      more: isMore,
      entryId: id,
    });
  };

  const handleSaveAssessment = (
    assessmentID,
    slug,
    name,
    time,
    callToAction,
    saved,
  ) => {
    let nodeID;
    if (saved)
      [{ nodeID }] = savedAssessments.filter(el => el.sys.id === assessmentID);

    const data = {
      nodeID,
      assessment: {
        assessmentID,
        slug,
        name,
        callToAction: callToAction || '',
        time: time || '',
      },
      save: !saved,
    };

    trackFavoriteAction(
      'Assessment Page',
      {
        assessmentID,
        slug,
        name,
      },
      auth,
      saved,
    );

    dispatch(saveAssessment(data));
  };

  if (_isEmpty(assessment)) return null;
  const {
    fields: { name, intro, time, callToAction, slug },
    sys: { id },
  } = assessment.items[0];
  const { color } = getColorsConfig(assessment.items[0]);
  const saved = isAssessmentSaved(savedAssessments, assessment.items[0]);
  const minuteLabel =
    time > 1
      ? _get(assessmentSiteCopy, 'pageCopy.minutePlural')
      : _get(assessmentSiteCopy, 'pageCopy.minuteSingular');
  return (
    <div className={classes.topContainer}>
      <AppContainer spacing={0}>
        <Grid
          item
          xs={12}
          sm={allowSignIn || shouldShowShareButton ? 7 : 'auto'}
          md={allowSignIn || shouldShowShareButton ? 8 : 'auto'}
          className={isMobile ? classes.mobileView : null}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="body2" className={classes.badge}>
                {_get(assessmentSiteCopy, 'pageCopy.assessmentLabel')}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h1" className={classes.title} align="left">
                {name}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <div className={classes.timeWrapper}>
                <ScheduleIcon />
                <Typography variant="overline" className={classes.time}>
                  {`${_get(
                    assessmentSiteCopy,
                    'pageCopy.takesOnlyLabel',
                  )} ${time} ${minuteLabel}`}
                </Typography>
              </div>
            </Grid>
            {isMobile && (
              <Grid
                item
                xs={12}
                className={classes.startButtonContainer}
                ref={startButtonRef}
              >
                <Button
                  className={classes.startButton}
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={onStartButtonClick}
                >
                  <Typography variant="button">
                    {_get(assessmentSiteCopy, 'pageCopy.startAssessmentLabel')}
                  </Typography>
                  <img
                    src={TakeArrowWhite}
                    alt="start-assessment"
                    className={classes.takeArrow}
                  />
                </Button>
              </Grid>
            )}
            {isMobile && (
              <>
                <div ref={clipContainerRef} className={classes.clipContainer} />
                <div ref={blockRef} />
              </>
            )}
            <PageIntroBlock
              type="reference"
              slug={slug}
              page="assessments"
              color={color.font}
            />
            <Grid item xs={12} className={classes.introText} id="intro-text">
              <RichTextReactRenderer
                color={color.font}
                limit={255}
                data={intro}
                readMoreLessHandler={handleReadMoreLess}
              />
            </Grid>
          </Grid>
          <ContentBlock
            type="item-page"
            id={_get(assessment, 'items.0.sys.id')}
            position={CONTENT_BLOCK_POSITIONS.BELOW_HERO_INTRO}
            classes={{
              item: classes.contentBlockItem,
            }}
            excludedTypes={[CONTENT_BLOCK_TYPES.ACCORDION]}
            marginBottom={false}
          />
        </Grid>
        {(allowSignIn || shouldShowShareButton) && (
          <Grid
            item
            xs={12}
            sm={5}
            md={4}
            className={isMobile ? classes.topIcon : undefined}
          >
            <Grid container direction="row" justifyContent="flex-end">
              {allowSignIn && (
                <Grid
                  item
                  xs={!mobileShare && !isAdmin && 7}
                  className={
                    !mobileShare && !isAdmin
                      ? classes.shareAndLikeWrapper
                      : classes.iconShareWrapper
                  }
                >
                  <ReactTooltip
                    id="SaveAsFavorite"
                    place="top"
                    type="light"
                    border
                  >
                    {saved
                      ? _get(
                          assessmentSiteCopy,
                          'pageCopy.removeFromFavoritesLabel',
                        )
                      : _get(
                          assessmentSiteCopy,
                          'pageCopy.saveAsFavoriteLabel',
                        )}
                  </ReactTooltip>
                  <IconButton
                    classes={{
                      root:
                        !mobileShare && !isAdmin
                          ? classes.button
                          : classes.iconBtn,
                    }}
                    onClick={() =>
                      handleSaveAssessment(
                        id,
                        slug,
                        name,
                        time,
                        callToAction,
                        saved,
                      )
                    }
                    disableRipple
                    aria-label="Save As Favorite"
                    data-tip
                    data-for="SaveAsFavorite"
                  >
                    {saved ? (
                      <img
                        src={HeartFullIcon}
                        alt="Full"
                        className={classes.favorite}
                      />
                    ) : (
                      <img
                        src={
                          mobileShare || isAdmin
                            ? HeartEmptyMobileIcon
                            : HeartEmptyIcon
                        }
                        alt="Empty"
                        className={classes.favorite}
                      />
                    )}
                    {!mobileShare && !isAdmin && (
                      <Typography className={classes.saveAsText}>
                        {saved
                          ? _get(
                              assessmentSiteCopy,
                              'pageCopy.removeFromFavoritesLabel',
                            )
                          : _get(
                              assessmentSiteCopy,
                              'pageCopy.saveAsFavoriteLabel',
                            )}
                      </Typography>
                    )}
                  </IconButton>
                  {isAdmin && (
                    <>
                      <ReactTooltip
                        id="ContentfulSetting"
                        place="top"
                        type="light"
                        border
                      >
                        See this entry on Contentful
                      </ReactTooltip>
                      <IconButton
                        classes={{ root: classes.iconBtn }}
                        disableRipple
                        aria-label="Setting"
                        data-tip
                        data-for="ContentfulSetting"
                      >
                        <a
                          href={getContentfulEntryUrl(id)}
                          target="_blank"
                          rel="noopener"
                          style={{
                            display: 'inline-flex',
                            color: 'white',
                          }}
                          aria-label="Setting"
                        >
                          <SettingsIcon className={classes.settings} />
                        </a>
                      </IconButton>
                    </>
                  )}
                </Grid>
              )}
              {shouldShowShareButton && (
                <Grid
                  item
                  xs={!mobileShare && !isAdmin && 4}
                  className={
                    !mobileShare && !isAdmin
                      ? classes.shareAndLikeWrapper
                      : classes.iconShareWrapper
                  }
                >
                  <ShareWithDropdown
                    id={id}
                    name={name}
                    isAdmin={isAdmin}
                    type="assessment"
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
        )}
      </AppContainer>
    </div>
  );
};

Header.propTypes = {
  isAdmin: PropTypes.bool,
  isMobile: PropTypes.bool,
  mobileShare: PropTypes.bool,
  onStartButtonClick: PropTypes.func,
};

const mapSizesToProps = ({ width }) => ({
  mobileShare: width < 750,
  isMobile: width < 600,
});

export default withSizes(mapSizesToProps)(Header);
